import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchSuggestions } from '../services/dataService';

const DEBOUNCE_MS = 400;

/**
 * Hook for fetching autocomplete suggestions with debouncing and request cancellation.
 *
 * @param {Object} options
 * @param {string} options.type - 'brand' | 'category' | 'size'
 * @param {string} options.query - search prefix
 * @param {string} options.brand - filter by brand (for category/size)
 * @param {string} options.category - filter by category (for size)
 * @param {boolean} options.enabled - whether to fetch (default true)
 * @param {number} options.limit - max results (default 20)
 * @returns {{ suggestions: string[], isLoading: boolean, isTyping: boolean, error: string|null, refetch: () => void }}
 */
export function useSuggestions({
  type,
  query = '',
  brand = '',
  category = '',
  enabled = true,
  limit = 20,
} = {}) {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);

  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const prevQueryRef = useRef(query);

  const doFetch = useCallback(async () => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const results = await fetchSuggestions({
        type,
        q: query,
        brand,
        category,
        limit,
        signal: abortControllerRef.current.signal,
      });

      setSuggestions(results);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[useSuggestions] Error:', err);
        setError(err.message || 'Failed to fetch suggestions');
      }
    } finally {
      setIsLoading(false);
    }
  }, [type, query, brand, category, limit]);

  useEffect(() => {
    if (!enabled || !type) {
      setSuggestions([]);
      setIsTyping(false);
      return;
    }

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set typing state immediately when query changes (but keep existing results visible)
    const queryChanged = query !== prevQueryRef.current;
    prevQueryRef.current = query;
    if (queryChanged) {
      setIsTyping(true);
    }

    // Debounce the fetch
    debounceTimerRef.current = setTimeout(() => {
      setIsTyping(false);
      doFetch();
    }, DEBOUNCE_MS);

    // Cleanup on unmount or dependency change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [enabled, type, query, brand, category, limit, doFetch]);

  // Manual refetch function
  const refetch = useCallback(() => {
    if (enabled && type) {
      doFetch();
    }
  }, [enabled, type, doFetch]);

  return { suggestions, isLoading, isTyping, error, refetch };
}

export default useSuggestions;
